import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { mediaItems, mediaCountries } from "@/data/media";
import { API } from "@/lib/api";

const countryAccent = {
  Kenya: "from-emerald-400 to-brand",
  Ghana: "from-yellow-400 to-red-500",
  Tanzania: "from-cyan-400 to-blue-500",
  Nigeria: "from-green-500 to-emerald-700",
  AOCEMS: "from-brand via-emerald-300 to-teal-300",
};

const codeBadge = {
  Kenya: "KOCEMS",
  Ghana: "GOCEMS",
  Tanzania: "TOCEMS",
  Nigeria: "NIMACEMS",
  AOCEMS: "AOCEMS",
};

function HeroMedia({ total }) {
  const { t } = useTranslation();
  return (
    <section className="relative pt-32 pb-12 overflow-hidden bg-brand-dark text-white">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(144,184,73,0.20),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(45,212,191,0.10),transparent_60%)]" />
        <div className="absolute inset-0 tech-grid-dark opacity-40" />
      </div>

      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-3 gap-md items-end">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-2">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-brand/30 bg-brand/10 text-brand text-[11px] font-bold uppercase tracking-widest mb-6">
              <span className="material-symbols-outlined text-[14px]">campaign</span>
              {t("media.hero.eyebrow")}
            </span>
            <h1 className="font-h1 text-[40px] md:text-[56px] leading-[1.05] font-extrabold mb-4">
              {t("media.hero.title_a")}{" "}
              <span className="bg-gradient-to-r from-brand via-emerald-300 to-teal-300 bg-clip-text text-transparent">
                {t("media.hero.title_accent")}
              </span>
            </h1>
            <p className="text-lg text-zinc-300 max-w-2xl">{t("media.hero.intro")}</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="grid grid-cols-2 gap-3">
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{t("media.hero.articles")}</div>
              <div className="font-manrope font-extrabold text-3xl text-white">{total}</div>
            </div>
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{t("media.hero.countries")}</div>
              <div className="font-manrope font-extrabold text-3xl text-brand">{mediaCountries.filter((c) => c !== "All" && c !== "AOCEMS").length}</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function CountryPills({ active, onChange, counts }) {
  const { t } = useTranslation();
  return (
    <div className="sticky top-16 z-30 bg-cream/90 backdrop-blur-xl border-b border-zinc-200/50">
      <div className="max-w-[1280px] mx-auto px-6 py-4 flex flex-wrap gap-2">
        {mediaCountries.map((c) => {
          const isActive = active === c;
          const count = c === "All" ? Object.values(counts).reduce((a, b) => a + b, 0) : counts[c] || 0;
          const label = c === "All" ? t("media.filters.all") : c;
          return (
            <button
              key={c}
              data-testid={`media-filter-${c.toLowerCase()}`}
              onClick={() => onChange(c)}
              className={`px-4 py-2 rounded-full text-sm font-bold tracking-tight transition-all flex items-center gap-2 ${
                isActive ? "bg-brand-dark text-white shadow-md" : "bg-white text-primary border border-outline-variant hover:border-brand hover:text-brand"
              }`}
            >
              {label}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${isActive ? "bg-brand text-brand-dark" : "bg-surface-container text-on-surface-variant"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ArticleCard({ item, index }) {
  const { t } = useTranslation();
  const accent = countryAccent[item.country] || "from-brand to-emerald-400";
  // If a curated image is supplied on the item, use it directly and skip the OG-image fetch.
  const [imgUrl, setImgUrl] = useState(item.image || null);
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    if (item.image) return; // curated override wins
    let cancelled = false;
    axios
      .get(`${API}/og-image`, { params: { url: item.url }, timeout: 12000 })
      .then((r) => {
        if (!cancelled && r.data?.image_url) setImgUrl(r.data.image_url);
        else if (!cancelled) setImgFailed(true);
      })
      .catch(() => !cancelled && setImgFailed(true));
    return () => { cancelled = true; };
  }, [item.url, item.image]);

  return (
    <motion.a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`media-article-${index}`}
      initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: index * 0.04 }}
      className="group bg-white rounded-2xl border border-outline-variant overflow-hidden hover:border-brand hover:shadow-xl transition-all flex flex-col"
    >
      <div className={`h-1.5 bg-gradient-to-r ${accent}`} />
      <div className={`relative aspect-[16/9] overflow-hidden bg-gradient-to-br ${accent}`}>
        {imgUrl && !imgFailed ? (
          <img src={imgUrl} alt={item.title} onError={() => setImgFailed(true)} referrerPolicy="no-referrer" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white/95">
              <span className="material-symbols-outlined text-5xl drop-shadow">newspaper</span>
              <div className="font-manrope font-extrabold text-2xl mt-1 drop-shadow">{item.code}</div>
              <div className="text-xs uppercase tracking-widest opacity-90">{item.country}</div>
            </div>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        <div className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-white/90 backdrop-blur text-[10px] font-bold uppercase tracking-widest text-primary">
          {item.publication}
        </div>
        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-brand/90 backdrop-blur text-[10px] font-bold uppercase tracking-widest text-brand-dark">
          {item.code}
        </div>
      </div>

      <div className="p-md flex flex-col flex-1">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-on-surface-variant flex items-center gap-1">
            <span className="material-symbols-outlined text-[14px]">location_on</span>
            {item.country}
          </span>
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{item.date}</span>
        </div>

        <h3 className="font-h3 text-[20px] leading-tight text-primary mb-3 line-clamp-3">{item.title}</h3>

        <p className="text-sm text-on-surface-variant flex-1 mb-4">{item.summary}</p>

        <div className="flex items-center justify-between pt-3 border-t border-outline-variant/50">
          <span className="text-xs font-bold text-primary flex items-center gap-1.5">
            <span className="material-symbols-outlined text-brand text-[16px]">newspaper</span>
            {item.publication}
          </span>
          <span className="text-brand font-bold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
            {t("media.article.read")}
            <span className="material-symbols-outlined text-[16px]">north_east</span>
          </span>
        </div>
      </div>
    </motion.a>
  );
}

function CountrySection({ country, items }) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  const code = codeBadge[country];
  const featureLabel = items.length === 1 ? t("media.section.feature") : t("media.section.features");
  const sectionLabel = country === "AOCEMS" ? `${code} · ${t("media.section.aocems_label")}` : `${code} · ${country}`;
  return (
    <div className="mb-xl">
      <div className="flex items-end justify-between mb-md">
        <div>
          <span className="text-brand text-label-caps">{sectionLabel}</span>
          <h2 className="font-h2 text-h2 text-primary mt-1">{items.length} {featureLabel}</h2>
        </div>
        <div className={`hidden md:block h-1 flex-1 ml-md bg-gradient-to-r ${countryAccent[country]} rounded-full max-w-[280px]`} />
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-gutter">
        {items.map((it, i) => (
          <ArticleCard key={it.url} item={it} index={i} />
        ))}
      </div>
    </div>
  );
}

export default function Media() {
  const { t } = useTranslation();
  const [active, setActive] = useState("All");

  const counts = useMemo(() => {
    const c = {};
    mediaItems.forEach((m) => { c[m.country] = (c[m.country] || 0) + 1; });
    return c;
  }, []);

  const filtered = useMemo(() => {
    if (active === "All") return mediaItems;
    return mediaItems.filter((m) => m.country === active);
  }, [active]);

  const grouped = useMemo(() => {
    if (active === "All") {
      const order = mediaCountries.filter((c) => c !== "All");
      return order.map((country) => ({ country, items: mediaItems.filter((m) => m.country === country) }));
    }
    return [{ country: active, items: filtered }];
  }, [active, filtered]);

  return (
    <>
      <HeroMedia total={mediaItems.length} />
      <CountryPills active={active} onChange={setActive} counts={counts} />
      <section className="max-w-[1280px] mx-auto px-6 py-xl">
        <AnimatePresence mode="wait">
          <motion.div key={active} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35 }}>
            {grouped.map((g) => (
              <CountrySection key={g.country} country={g.country} items={g.items} />
            ))}

            {filtered.length === 0 && (
              <div className="text-center py-xl">
                <span className="material-symbols-outlined text-zinc-400 text-5xl mb-3">inbox</span>
                <p className="text-on-surface-variant">{t("media.empty")}</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </section>

      <section className="bg-primary-container text-white py-xl relative overflow-hidden">
        <div className="absolute -top-32 -right-24 w-[500px] h-[500px] rounded-full bg-brand/15 blur-3xl" />
        <div className="max-w-[1280px] mx-auto px-6 relative z-10 grid md:grid-cols-2 gap-md items-center">
          <div>
            <span className="text-brand text-label-caps">{t("media.cta.eyebrow")}</span>
            <h2 className="font-h2 text-h2 mt-2 mb-3">{t("media.cta.title")}</h2>
            <p className="text-zinc-300 max-w-xl">{t("media.cta.intro")}</p>
          </div>
          <div className="md:text-right">
            <a href="mailto:press@africaocems.com" data-testid="media-press-contact" className="inline-flex items-center gap-2 px-7 py-4 bg-brand text-brand-dark font-bold rounded-xl shadow-lg hover:-translate-y-0.5 transition-all">
              press@africaocems.com
              <span className="material-symbols-outlined">arrow_forward</span>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
